import { Component, EventEmitter, Input } from '@angular/core';
import { LoadingController, ModalController, NavController } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { PositionService } from 'src/app/services/position.service';
import { SegmentCMOSService } from 'src/app/services/segment-cmos.service';
import { UserService } from 'src/app/services/user.service';
import { getApplicationHomeQueryParamString } from 'src/app/util/job-util';
import { Blueshift, PositionDetail } from '../multi-select/multi-select.component';
import { ScheduleConfirmationComponent } from '../schedule-confirmation/schedule-confirmation.component';
import dayjs from 'dayjs';
import { TimeToStartConfirmModalComponent } from 'src/app/common/time-to-start-confirm-modal/time-to-start-confirm-modal.component';
import { ConfirmChoice } from 'src/app/util/types';
import { presentLoader } from 'src/app/util/ion-util';
import { RELOAD_POSITIONS_KEY } from 'src/app/util/storage-util';
import { AppRoutingUtil } from 'src/app/util/app-routing-util';

@Component({
  selector: 'app-get-these-shifts',
  templateUrl: './get-these-shifts.component.html',
  styleUrls: ['./get-these-shifts.component.scss'],
})
export class GetTheseShiftsComponent {

  @Input() directInviteBlueshifts: Blueshift[];
  @Input() modalId: string;
  @Input() positionDetail: PositionDetail;
  @Input() shiftsSelected: Blueshift[];

  loadingSpinner: HTMLIonLoadingElement;

  constructor(
    private readonly loadingCtrl: LoadingController,
    private readonly modalCtrl: ModalController,
    private readonly navCtrl: NavController,
    private readonly positionService: PositionService,
    private readonly segmentCMOS: SegmentCMOSService,
    private readonly storage: Storage,
    private readonly userService: UserService) { }

  getTheseShiftsButtonText() {
    return `Get these shifts (${this.shiftsSelected.length})`;
  }

  getRejectDirectInvitesButtonText() {
    return `Reject all direct invite shifts (${this.directInviteBlueshifts.length})`;
  }

  async getTheseShifts() {
    const eventEmitter = new EventEmitter();
    const scheduleConfirmationId = 'schedule-confirmation-modal'
    const m = await this.modalCtrl.create({
      component: ScheduleConfirmationComponent,
      cssClass: 'bottom-sheet-modal-presented-above-modal',
      componentProps: {
        blueshifts: this.shiftsSelected,
        directInvitesToBeRejected: this.getDirectInvitesToBeRejected(),
        confirmEvent: eventEmitter,
        modalId: scheduleConfirmationId
      },
      id: scheduleConfirmationId
    });
    eventEmitter.subscribe(() => {
      if (!this.shiftStartsWithin48Hours()) {
        this.directUserToJob();
      }
    });
    await m.present();
  }

  async rejectAllDirectInvites() {
    this.loadingSpinner = await presentLoader(this.loadingCtrl);
    this.rejectDirectInvites(this.directInviteBlueshifts);
    this.storage.set(RELOAD_POSITIONS_KEY, true).then(() => {
      this.modalCtrl.dismiss(undefined, undefined, this.modalId);
      this.loadingSpinner.dismiss();
      this.navCtrl.navigateBack(AppRoutingUtil.getPositionRoute(this.positionDetail.positionId.toString()));
    });
  }

  shiftStartsWithin48Hours(): boolean {
    // assumes shiftsSelected are ordered by start date
    if (dayjs(this.shiftsSelected[0].startDateTime).isBefore(dayjs().add(48, 'hour'))) {
      this.confirmStartTime(this.shiftsSelected[0].startDateTime);
      return true;
    }
    return false;
  }

  async confirmStartTime(startTimestamp: string) {
    const modal = await this.modalCtrl.create({
      component: TimeToStartConfirmModalComponent,
      cssClass: 'bottom-sheet-modal-presented-above-modal',
      componentProps: {
        startTimestamp: startTimestamp,
        cameFromMultiSelect: true
      }
    });
    await modal.present();
    const { data: confirmChoice } = await modal.onWillDismiss<ConfirmChoice>();
    if (confirmChoice === 'confirm') {
      this.directUserToJob();
    } // else do nothing, let modal close and user will still see multi-select
  }

  private getDirectInvitesToBeRejected() {
    return this.directInviteBlueshifts.filter(directInviteBlueshift => {
      return !this.shiftsSelected.includes(directInviteBlueshift);
    });
  }

  private async directUserToJob() {
    // reject remaining unselected Direct Invites
    const directInvitesToBeRejected = this.getDirectInvitesToBeRejected();

    this.loadingSpinner = await presentLoader(this.loadingCtrl);
    this.rejectDirectInvites(directInvitesToBeRejected);
    this.loadingSpinner.dismiss();

    // create job application for selected shifts
    const jobIds = this.getJobIds(this.shiftsSelected);
    const jobIdsJoinedBySpace = jobIds.join(' ');
    if (this.userService.currentUser) {
      this.segmentCMOS.addEvent('acceptBlueshiftJobs', { jobIds: jobIdsJoinedBySpace });
      this.navCtrl.navigateBack(`/application-home${getApplicationHomeQueryParamString(jobIds)}`);
    } else {
      this.segmentCMOS.addEvent('anonAcceptBlueshiftJobs', { jobIds: jobIdsJoinedBySpace });
      this.userService.setJobIds(jobIds);
      this.navCtrl.navigateRoot(AppRoutingUtil.ACCOUNT_REGISTER);
    }
    await this.modalCtrl.dismiss(undefined, undefined, this.modalId);
  }

  private getJobIds(shiftsSelected: Blueshift[]): string[] {
    return shiftsSelected.map((shiftSelected) => shiftSelected.externalJobId);
  }

  private rejectDirectInvites(directInvitesToReject: Blueshift[]) {
    const rejectionReason = 'Schedule won\'t work';
    directInvitesToReject.forEach(directInvite => {
      this.positionService.rejectJob(directInvite.externalJobId, rejectionReason)
        .toPromise()
        .then(() => {
          this.segmentCMOS.addEvent('rejectJob_blueshiftDirectInvite',
            { jobId: directInvite.externalJobId, reason: rejectionReason });
        })
        .catch(error => {
          this.segmentCMOS.addEvent('rejectJob_blueshiftDirectInvite_failed',
            { jobId: directInvite.externalJobId, reason: rejectionReason, error: error });
        });
    });
  }
}
